import { useEffect } from 'react';
import { Buttons } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { showChat } from 'Services/chat';
import { history } from 'Store';
import { useTranslation } from 'Hooks/useTranslation';
import { useFetchGetUserProfile, useGetUserPlan } from 'Containers/Result/hooks';
import {
  handleRedirectUser,
  sendTaxReturnResultViewEvent,
  TaxReturnTypes,
} from 'Containers/Result/utils';
import { useGetPreviousYearInfo } from 'Containers/Filing/hooks';
import { routes } from 'Constants/routes';
import { FRONT_PLANS } from 'Constants/plans';
import { ResultType, TaxProfile, taxReturnFilingType } from 'Types/common';
import ResultInfo from '../ResultInfo';
import ResultInfoNotToReturn from '../ResultInfoNotToReturn';
import { isMaiorAAResultAndIsToPay } from '../ResultInfoNotToReturn/utils';
import * as Styled from './styles';
import { getContextLabel } from './utils';

const FLEX_LITE_CONTEXT = 'isToPayLite';
const FLEX_LITE_MANDATORY_CONTEXT = 'isToPayLiteMandatory';

export type NotToReturnProps = {
  data: ResultType;
  flowEnabled: boolean;
  showPreviousYearBanner: boolean;
};
const ResultBoxNotReturn = ({
  data,
  flowEnabled,
  showPreviousYearBanner,
}: NotToReturnProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.RESULT);
  const plan = useGetUserPlan();
  const previousYearInfo = useGetPreviousYearInfo();
  const userComplex = useFetchGetUserProfile();

  const notHavePreviousYearAndNotIsComplex =
    !showPreviousYearBanner && userComplex?.taxProfile !== TaxProfile.COMPLEX;

  const isToPayAndNotMandatory = data.taxReturnType === TaxReturnTypes.TO_PAY && !data.mandatory;

  const isEqualAndMandatory = data.taxReturnType === TaxReturnTypes.EQUAL && data.mandatory;

  const isToPayLite =
    isEqualAndMandatory ||
    (isToPayAndNotMandatory && notHavePreviousYearAndNotIsComplex && plan === FRONT_PLANS.BASIC) ||
    (data?.taxReturnType === TaxReturnTypes.EQUAL &&
      notHavePreviousYearAndNotIsComplex &&
      plan === FRONT_PLANS.BASIC);

  const redirectUserToPreFiling =
    showPreviousYearBanner &&
    userComplex?.taxProfile !== TaxProfile.COMPLEX &&
    !isMaiorAAResultAndIsToPay(data, previousYearInfo);

  const handleClick = (): void => {
    if (isToPayLite && plan === FRONT_PLANS.BASIC) {
      history.push(routes.FLEX_LITE, {
        taxReturnType: data?.taxReturnType,
        taxReturnResult: data?.taxReturnResult,
        fromFedUser: data?.taxReturnFilingType === taxReturnFilingType.complementary,
        notIsToReturn: data.taxReturnType === TaxReturnTypes.EQUAL && data.mandatory,
        mandatory: data?.mandatory,
      });
    } else if (redirectUserToPreFiling && plan === FRONT_PLANS.BASIC) {
      history.push(routes.PRE_FILING);
    } else if (plan === FRONT_PLANS.FLEX_LITE && !data.mandatory && showPreviousYearBanner) {
      history.push(routes.PREVIOUS_YEARS_FILTER);
    } else {
      handleRedirectUser({ plan, data });
    }
  };

  const renderCrossSellAA =
    redirectUserToPreFiling && (plan === FRONT_PLANS.BASIC || plan === FRONT_PLANS.FLEX_LITE);

  const contextLabel = getContextLabel(data, renderCrossSellAA);

  useEffect(() => {
    if (data) {
      sendTaxReturnResultViewEvent(data, previousYearInfo, renderCrossSellAA);
    }
  }, [data]);

  return (
    <Styled.Box type={data?.taxReturnType} direction="column" align="center">
      <Styled.BoxContent direction="column" align="center" justify="center" gap="dimension_16">
        {renderCrossSellAA ? (
          <ResultInfoNotToReturn data={data} />
        ) : (
          <ResultInfo data={data} isToPayLite={isToPayLite} />
        )}
        {!flowEnabled ? (
          <Buttons.Classic
            label={t('Result.notReturn.button', {
              context: isToPayLite
                ? data.mandatory
                  ? FLEX_LITE_MANDATORY_CONTEXT
                  : FLEX_LITE_CONTEXT
                : contextLabel,
            })}
            mode="primary"
            size="m"
            onClick={handleClick}
          />
        ) : (
          <Styled.ButtonsContainer direction="column">
            <Buttons.Classic
              label={t('Result.contact.asist')}
              mode="success"
              size="s"
              onClick={showChat}
            />
          </Styled.ButtonsContainer>
        )}
      </Styled.BoxContent>
    </Styled.Box>
  );
};

export default ResultBoxNotReturn;
